<template>
  <div
    v-if="fixedWidth"
    class="w-100 d-flex align-items-center justify-content-center"
  >
    <span class="countdown-digital">
      {{ displayRemainingTime }}
    </span>
  </div>
  <span v-else class="countdown-digital">
    {{ displayRemainingTime }}
  </span>
</template>

<script>
export default {
  name: "CountdownDigital",
  props: {
    time: {
      type: Number,
      required: true,
    },
    prefix: {
      type: String,
      default: "",
    },
    daysToMinutes: {
      type: Boolean,
      default: false,
    },
    daysToHours: {
      type: Boolean,
      default: false,
    },
    // adds a full-width wrapper element and centers countdown <span> as to avoid "jiggling"
    fixedWidth: {
      type: Boolean,
      default: false,
    },
    withTimeDiff: {
      type: Boolean,
      default: false,
    },
    // Omit the seconds group from the default (non daysToHours/Minutes) formats.
    hideSeconds: {
      type: Boolean,
      default: false,
    },
    // Show days+hours only (e.g. "5D 14H" / "14H"); drop to minutes ("45M")
    // only once under an hour. Takes precedence over the other formats.
    hideMinutes: {
      type: Boolean,
      default: false,
    },
    // Show only the largest unit: days ("5D"), else hours ("14H") under a day,
    // else minutes ("45M") under an hour. Takes precedence over other formats.
    singleUnit: {
      type: Boolean,
      default: false,
    },
    twoSegments: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      now: Date.now(),
      intervalId: null,
    };
  },
  computed: {
    timeDiff() {
      return this.$store.getters["user/getTimeDifference"];
    },

    displayRemainingTime() {
      if (this.time === 0) {
        clearInterval(this.intervalId);
        return;
      }

      let t = this.time - this.now;
      if (this.withTimeDiff) {
        t -= this.timeDiff;
      }

      let days = Math.floor(t / (1000 * 60 * 60 * 24));
      let hours = Math.floor((t % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      let minutes = Math.floor((t % (1000 * 60 * 60)) / (1000 * 60));
      let seconds = Math.floor((t % (1000 * 60)) / 1000);
      if (t < 0) {
        this.$emit("over");
        // window.clearInterval(this.intervalId);
        return this.$t("countdown.now");
      } else if (this.twoSegments) {
        if (t >= 86400000) return this.prefix + days + "d " + hours + "h";
        if (t >= 3600000) return this.prefix + hours + "h " + minutes + "m";
        if (t >= 60000) return this.prefix + minutes + "m " + seconds + "s";
        return this.prefix + seconds + "s";
      } else if (this.singleUnit) {
        // Largest unit only: days → hours → minutes. Abbreviations are always
        // the English D/H/M (not localized) — the offer-chain badge uses these.
        if (t >= 86400000) {
          return this.prefix + days + "D";
        }
        if (t >= 3600000) {
          return this.prefix + hours + "H";
        }
        return this.prefix + minutes + "M";
      } else if (this.hideMinutes) {
        const pad2 = (n) => ("0" + n).slice(-2);
        if (t < 60000) {
          return this.prefix + seconds + "s";
        }
        if (t < 3600000) {
          return this.prefix + minutes + "m";
        }
        if (days > 0) {
          return this.prefix + days + "d " + pad2(hours) + "h";
        }
        return this.prefix + hours + "h";
      } else if (this.daysToHours) {
        // e.g. for offer badge countdown
        if (hours < 1) {
          if (days <= 1) {
            return days + " " + this.$t("time.day.one");
          }
          return days + " " + this.$t("time.day.other");
        }
        return (
          days +
          this.$t("time.day.abbr") +
          " " +
          hours +
          " " +
          this.$t("time.hour.abbr_alt")
        );
      } else if (this.daysToMinutes) {
        return (
          // e.g. for event countdown

          ("00" + days).slice(-2) +
          ":" +
          ("00" + hours).slice(-2) +
          ":" +
          ("00" + minutes).slice(-2)
        );
        // countdown in hours to seconds up to 48 hours
      } else if (t > 86400000 && t < 172800000) {
        hours += days * 24;
        const ss = this.hideSeconds ? "" : ":" + ("00" + seconds).slice(-2);
        return (
          // Formatted so that leading zeros aren't dropped
          this.prefix +
          ("00" + hours).slice(-2) +
          ":" +
          ("00" + minutes).slice(-2) +
          ss
        );
        // countdown days to seconds for everything over 48 hours
      } else if (t > 172800000) {
        const ss = this.hideSeconds ? "" : ":" + ("00" + seconds).slice(-2);
        return (
          // Formatted so that leading zeros aren't dropped
          this.prefix +
          ("00" + days).slice(-2) +
          "T, " +
          ("00" + hours).slice(-2) +
          ":" +
          ("00" + minutes).slice(-2) +
          ss
        );
      } else if (t < 600000 || ("00" + hours).slice(-2) === "00") {
        // Under an hour: mm:ss, or hh:mm ("00:mm") when seconds are hidden.
        if (this.hideSeconds) {
          return (
            this.prefix +
            ("00" + hours).slice(-2) +
            ":" +
            ("00" + minutes).slice(-2)
          );
        }
        return (
          this.prefix +
          ("00" + minutes).slice(-2) +
          ":" +
          ("00" + seconds).slice(-2)
        );
      } else {
        const ss = this.hideSeconds ? "" : ":" + ("00" + seconds).slice(-2);
        return (
          this.prefix +
          ("00" + hours).slice(-2) +
          ":" +
          ("00" + minutes).slice(-2) +
          ss
        );
      }
    },
  },
  created() {
    let self = this;
    this.intervalId = setInterval(() => {
      self.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
  },
};
</script>
<style lang="scss">
.countdown-digital {
  font-family: Ubuntu-Bold;
}
</style>
